import { io, Socket } from 'socket.io-client';
import { getStoredUser } from './auth';
import { getDocumentBlob } from './wordDraft';
import type { DocumentAnalysisResult, DocumentRisk, RiskType } from './analysis';
import {
  SIGNUS_API_URL,
  WSS_PATH,
  WORD_RISK_ANALYSIS_WSS_NAMESPACE,
  WORD_RISK_ANALYSIS_WSS_EVENT,
} from './constants';
import { ApiError, extractErrorMessage, extractErrorType, fetchWithTokenRefresh } from './apiFetch';

interface RiskItemDto {
  id: string;
  title: string;
  description: string;
  level: string;
  excerpt: string | null;
  page: number | null;
  impact: string;
  suggestedChange: string;
  suggestedExcerpt: string | null;
}

interface RiskAnalysisResultDto {
  summary: string | null;
  legalRisks: RiskItemDto[];
  businessRisks: RiskItemDto[];
  structuralRisks: RiskItemDto[];
}

interface WssStatusPayload {
  sessionId: string;
  status: 'COMPLETED' | 'FAILED';
  errorMessage?: string;
}

interface WssMessage {
  payload: WssStatusPayload;
}

async function createSession(blob: Blob, teamSlug: string, accessToken: string): Promise<string> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/risk-analysis/sessions`,
    accessToken,
    token => {
      const form = new FormData();
      form.append('file', blob, 'document.docx');
      return { method: 'POST', headers: { Authorization: token }, body: form };
    },
  );

  const json = await res.json();

  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Failed to create analysis session (${res.status})`),
      extractErrorType(json),
    );
  }

  const sessionId = json.sessionId ?? (json.payload?.sessionId as string | undefined);
  if (!sessionId) throw new Error('Server did not return a sessionId');
  return sessionId as string;
}

function waitForCompletion(
  sessionId: string,
  accessToken: string,
  teamSlug: string,
  timeoutMs = 5 * 60 * 1000,
): Promise<void> {
  const wssUrl = `${SIGNUS_API_URL}/wss${WORD_RISK_ANALYSIS_WSS_NAMESPACE}`;

  return new Promise((resolve, reject) => {
    let settled = false;

    const socket: Socket = io(wssUrl, {
      path: WSS_PATH,
      transports: ['websocket'],
      auth: { token: getStoredUser()?.accessToken ?? accessToken, teamSlug, sessionId },
    });

    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        socket.disconnect();
        reject(new Error('Analysis timed out. Please try again.'));
      }
    }, timeoutMs);

    socket.on('connect_error', (err: Error) => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        socket.disconnect();
        reject(new Error(`WebSocket connection failed: ${err.message}`));
      }
    });

    socket.on(WORD_RISK_ANALYSIS_WSS_EVENT, (msg: WssMessage) => {
      const p = msg?.payload;
      if (!p || p.sessionId !== sessionId) return;

      settled = true;
      clearTimeout(timer);
      socket.disconnect();

      if (p.status === 'COMPLETED') {
        resolve();
      } else {
        reject(new Error(p.errorMessage || 'Analysis failed on the server.'));
      }
    });
  });
}

async function fetchResults(
  sessionId: string,
  teamSlug: string,
  accessToken: string,
): Promise<RiskAnalysisResultDto> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/risk-analysis/sessions/${encodeURIComponent(sessionId)}/results`,
    accessToken,
    token => ({ headers: { Authorization: token } }),
  );

  const json = await res.json();

  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Failed to fetch results (${res.status})`),
      extractErrorType(json),
    );
  }

  return (json.payload ?? json) as RiskAnalysisResultDto;
}

function mapRisks(items: RiskItemDto[], type: RiskType): DocumentRisk[] {
  return items.map(item => ({
    id: item.id,
    type,
    title: item.title,
    description: item.description,
    snippet: item.excerpt,
    suggestedAlternative: item.suggestedExcerpt ?? null,
  }));
}

function mapResult(dto: RiskAnalysisResultDto): DocumentAnalysisResult {
  const risks: DocumentRisk[] = [
    ...mapRisks(dto.legalRisks ?? [], 'legal'),
    ...mapRisks(dto.businessRisks ?? [], 'business'),
    ...mapRisks(dto.structuralRisks ?? [], 'structural'),
  ];
  return {
    summary: dto.summary ?? 'Analysis complete.',
    risks,
  };
}

export async function analyzeDocumentViaService(): Promise<DocumentAnalysisResult> {
  const user = getStoredUser();
  if (!user) {
    throw new Error('You must be signed in to Signus to run a risk analysis.');
  }

  const { accessToken, teamSlug } = user;

  const blob = await getDocumentBlob();
  const sessionId = await createSession(blob, teamSlug, accessToken);

  await waitForCompletion(sessionId, accessToken, teamSlug);

  const dto = await fetchResults(sessionId, teamSlug, accessToken);
  return mapResult(dto);
}
